import { useId, useState, type FC, type FormEvent } from "react";
import { ChevronLeft } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { ONBOARDING_RELATIONSHIP_OPTIONS } from "@/shared/constants/relationshipOptions";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { splitName } from "@/shared/utils/names";

interface AddPersonFormProps {
  initialName: string;
  onCancel: () => void;
  onAdded: (contactId: number) => void;
}

export const AddPersonForm: FC<AddPersonFormProps> = ({ initialName, onCancel, onAdded }) => {
  const id = useId();
  const { person } = useAppContext();
  const { createContactAsync, isCreating } = useUserContacts();
  const initial = splitName(initialName);
  const [firstName, setFirstName] = useState(initial.firstName);
  const [lastName, setLastName] = useState(initial.surName);
  const [relationship, setRelationship] = useState("");
  const [error, setError] = useState<string>();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setError(undefined);
    try {
      const contact = await createContactAsync({
        first_name: firstName.trim(),
        last_name: lastName.trim() || null,
        relationship: relationship || null,
      });
      onAdded(contact.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add this person. Try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-center gap-2 border-b border-primary px-3 py-2.5">
        <button
          type="button"
          aria-label="Back to people"
          className="text-tertiary"
          onClick={onCancel}
        >
          <ChevronLeft className="size-4" />
        </button>
        <p className="font-medium">Add person</p>
      </div>
      <div className="flex flex-col gap-3 p-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-first`}>First name</Label>
            <Input
              id={`${id}-first`}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-last`}>Last name</Label>
            <Input
              id={`${id}-last`}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-relationship`}>
            Relationship to {person?.first_name || "the decedent"}
          </Label>
          <Select value={relationship} onValueChange={setRelationship}>
            <SelectTrigger id={`${id}-relationship`} className="w-full font-normal">
              <SelectValue placeholder="Select relationship" />
            </SelectTrigger>
            {/* Opens inside the PeopleSelector popover, which sits at z-[100] */}
            <SelectContent className="z-[200]">
              {ONBOARDING_RELATIONSHIP_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <FieldError message={error} />
      </div>
      <div className="flex justify-end gap-2 border-t border-primary px-3 py-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" variant="brand" disabled={!firstName.trim() || isCreating}>
          {isCreating ? "Adding..." : "Add and select"}
        </Button>
      </div>
    </form>
  );
};
